import { AnyAction } from 'redux';
import { AppDispatch } from '../../../../commons/store/store';
import { FormErrors, reset } from 'redux-form';
import moment from 'moment/moment';
import apiHelper from '../../../../api/apiHelper';
import { startToast } from '../../../../commons/components/toast/toastDucks';
import { WagonTemplate } from '../../../../commons/model/templates';
import wagonCache from '../../../../commons/templates/wagonCache';
import { loadLibrary, startEditWagon } from '../../vehiclesLibraryDuck';
import { RecommissioningReport } from './recommissioningReportTypes';
import { ValidationErrors } from '../../../../commons/validation/validationUtils';

export const OPEN_RECOMMISSIONING_REPORT_DIALOG = 'OPEN_RECOMMISSIONING_REPORT_DIALOG';
export const CLOSE_RECOMMISSIONING_REPORT_DIALOG = 'CLOSE_RECOMMISSIONING_REPORT_DIALOG';
export const ADD_RECOMMISSIONING_REPORT_FILE = 'ADD_RECOMMISSIONING_REPORT_FILE';
export const REMOVE_RECOMMISSIONING_REPORT_FILE = 'REMOVE_RECOMMISSIONING_REPORT_FILE';
export const UPDATE_RECOMMISSIONING_REPORT = 'UPDATE_RECOMMISSIONING_REPORT';

export type DIALOG_TYPE = 'CREATION' | 'EDIT_REPORT_FILE' | 'CANCEL';

export type RecommissioningReportFormValues = {
  wagonTemplateId: number | null;
  wagonTemplateRegistration: string | null;
  reportId: string | null;
  reportDate: moment.Moment | null;
  reportS3key: String | null;
  reportFileName: String | null;
  pendingFile: File | null;
  cancelComment: string | null;
};

export type RecommissioningReportState = {
  formData: RecommissioningReportFormValues;
  hidden: boolean;
  onClose: () => void;
  dialogType: DIALOG_TYPE;
};

const initialState: RecommissioningReportState = {
  formData: {
    wagonTemplateId: null,
    wagonTemplateRegistration: null,
    reportId: null,
    reportDate: null,
    reportS3key: null,
    reportFileName: null,
    pendingFile: null,
    cancelComment: null,
  },
  hidden: true,
  onClose: () => null,
  dialogType: 'CREATION',
};

export default (state = initialState, action: AnyAction): RecommissioningReportState => {
  switch (action.type) {
    case OPEN_RECOMMISSIONING_REPORT_DIALOG:
      return {
        ...initialState,
        hidden: false,
        dialogType: action.dialogType,
        formData: { ...initialState.formData, ...action.payload },
      };
    case CLOSE_RECOMMISSIONING_REPORT_DIALOG:
      return { ...initialState, hidden: true };
    case ADD_RECOMMISSIONING_REPORT_FILE:
      return { ...state, formData: { ...state.formData, pendingFile: action.payload } };
    case REMOVE_RECOMMISSIONING_REPORT_FILE:
      return { ...state, formData: { ...state.formData, pendingFile: null } };
    case UPDATE_RECOMMISSIONING_REPORT:
      return { ...state, formData: { ...state.formData, ...action.payload } };
    default:
      return state;
  }
};

export const openRecommissioningReportDialog =
  (wagonTemplate: WagonTemplate, recommissioningReport: RecommissioningReport | null, dialogType: DIALOG_TYPE) =>
  (dispatch: AppDispatch) => {
    const basicPayload = {
      wagonTemplateId: wagonTemplate.id,
      wagonTemplateRegistration: wagonTemplate.registration,
    };

    dispatch({
      type: OPEN_RECOMMISSIONING_REPORT_DIALOG,
      dialogType,
      payload: recommissioningReport
        ? {
            ...basicPayload,
            reportId: recommissioningReport.id,
            reportDate: moment(recommissioningReport.recommissioningReportDate),
            reportS3key: recommissioningReport.s3Key,
            reportFileName: recommissioningReport.fileName,
          }
        : basicPayload,
    });
  };

export const closeRecommissioningReportDialog = () => (dispatch: AppDispatch) => {
  dispatch({
    type: CLOSE_RECOMMISSIONING_REPORT_DIALOG,
  });
  dispatch(reset('recommissioningReportForm'));
};

export const validateRecommissioningReportForm = (
  formValues: RecommissioningReportFormValues,
): FormErrors<RecommissioningReportFormValues> => {
  const errors: ValidationErrors<keyof RecommissioningReportFormValues> = {};
  if (!formValues) {
    return errors;
  }

  if (!formValues.reportDate?.isSameOrBefore(moment())) {
    errors.reportDate = 'doit être une date dans le passé ou le présent';
  }

  return errors;
};

export const updateRecommissioningReport =
  ({ acceptedFiles, reportDate }: { acceptedFiles?: File[]; reportDate?: string }) =>
  (dispatch: AppDispatch) => {
    if (acceptedFiles) {
      dispatch({
        type: UPDATE_RECOMMISSIONING_REPORT,
        payload: {
          pendingFile: acceptedFiles.length > 0 ? acceptedFiles[0] : null,
        },
      });
    } else if (reportDate) {
      dispatch({
        type: UPDATE_RECOMMISSIONING_REPORT,
        payload: {
          reportDate: moment(reportDate),
        },
      });
    }
  };

export const removePendingRecommissioningReport = () => (dispatch: AppDispatch) => {
  dispatch({
    type: REMOVE_RECOMMISSIONING_REPORT_FILE,
  });
};

export const submitRecommissioningReportDialog =
  (values: RecommissioningReportFormValues, dialogType: DIALOG_TYPE) => (dispatch: AppDispatch) => {
    switch (dialogType) {
      case 'CREATION':
        const creationFormData = new FormData();
        creationFormData.append('wagonTemplateId', values.wagonTemplateId + '');
        creationFormData.append('recommissioningReportDate', values.reportDate!.local().format('yyyy-MM-DD'));
        if (values.pendingFile) {
          creationFormData.append('file', values.pendingFile);
        }

        const creationRequest = apiHelper.postRaw('/api/recommissioning-reports', creationFormData);
        return doPersist(
          values,
          creationRequest,
          'La déclaration a bien été ajoutée',
          "La déclaration n'a pas pu être ajoutée",
          dispatch,
        );

      case 'EDIT_REPORT_FILE':
        const formData = new FormData();
        if (values.pendingFile) {
          formData.append('file', values.pendingFile);
        }

        const ediRequest = apiHelper.postRaw(`/api/recommissioning-reports/${values.reportId}/file`, formData);
        return doPersist(
          values,
          ediRequest,
          'Le rapport ARES a été modifié avec succès',
          "Le rapport n'a pas été modifié",
          dispatch,
        );

      case 'CANCEL':
        const request = apiHelper.put(`/api/recommissioning-reports/${values.reportId}`, {
          cancelComment: values.cancelComment,
        });
        return doPersist(
          values,
          request,
          'La dernière déclaration de remise en service a bien été annulée',
          "La dernière déclaration n'a pas pu être annulée",
          dispatch,
        );
      default:
        return null;
    }
  };

const doPersist = async (
  formValues: RecommissioningReportFormValues,
  persistRequest: Promise<void>,
  successToastMessage: string,
  errorToastMessage: string,
  dispatch: AppDispatch,
) => {
  try {
    await persistRequest;
    dispatch({ type: CLOSE_RECOMMISSIONING_REPORT_DIALOG });

    // refresh content in wagon details popup
    if (formValues.wagonTemplateRegistration) {
      apiHelper.get(`/api/wagon-templates/${formValues.wagonTemplateRegistration}`).then(async (savedTemplate) => {
        await wagonCache.addOrUpdateTemplatesFromLibrary([savedTemplate]);
        dispatch(loadLibrary());
        dispatch(startEditWagon(savedTemplate));
      });
    }

    dispatch(
      startToast({
        text: successToastMessage,
        className: 'success',
      }),
    );
  } catch (err) {
    dispatch(
      startToast({
        text: `Une erreur est survenue. ${errorToastMessage}`,
        className: 'error',
      }),
    );
  }
};
